import { defineMessages } from 'react-intl';

/**
 * Footer copy. The footer never uses react-intl at render time — it receives
 * pre-built strings from buildFooterStaticStrings, which looks these ids up
 * dynamically in the flat message catalogue. Because those lookups are dynamic,
 * `formatjs extract` cannot see them at the use site; this defineMessages block
 * is the extraction source that keeps every footer.* id in en.json. Without it,
 * extract:i18n drops the ids and the sync step deletes their fi/sv translations.
 *
 * The three language-row labels (Jobs in English / Työpaikat suomeksi / Lediga jobb
 * på svenska) are intentionally NOT here — like a language picker, each label is
 * always shown in its own target language regardless of viewer locale, so they're
 * literal constants in Footer.tsx rather than per-viewer-locale translations.
 */
export const footerMessages = defineMessages({
  copyright: {
    id: 'footer.copyright',
    defaultMessage: '© 2026 {company}',
    description: 'Copyright line with company name placeholder',
  },
  businessIdLabel: {
    id: 'footer.businessIdLabel',
    defaultMessage: 'Business ID',
    description: 'Label preceding the Finnish Y-tunnus business identifier in the legal bar',
  },
  aiTranslationDisclaimer: {
    id: 'footer.aiTranslationDisclaimer',
    defaultMessage:
      'Note: Some content on this website contains machine-translations using AI and may contain errors.',
    description: 'Disclaimer about AI-translated content shown in the footer',
  },
  brandDescription: {
    id: 'footer.brandDescription',
    defaultMessage:
      'JobCrawls is a job search engine for Finland. We index openings directly from thousands of employer career pages daily, in English, Finnish, and Swedish, with transparent salary data.',
    description: 'One-paragraph brand description shown in the footer brand strip',
  },
  colFindJobs: {
    id: 'footer.colFindJobs',
    defaultMessage: 'Find jobs',
    description: 'Footer column heading for job-search links',
  },
  colJobSeekers: {
    id: 'footer.colJobSeekers',
    defaultMessage: 'Job seekers',
    description: 'Footer column heading for job-seeker resource links',
  },
  colCompany: {
    id: 'footer.colCompany',
    defaultMessage: 'Company',
    description: 'Footer column heading for company/about links',
  },
  colEmployersAndDevelopers: {
    id: 'footer.colEmployersAndDevelopers',
    defaultMessage: 'Employers & developers',
    description: 'Footer column heading for employer and developer links',
  },
  openJobsInFinland: {
    id: 'footer.openJobsInFinland',
    defaultMessage: 'Open jobs in Finland',
    description: 'Footer link to the root job search results page',
  },
  jobsInHelsinki: {
    id: 'footer.jobsInHelsinki',
    defaultMessage: 'Jobs in Helsinki',
    description: 'Footer link to Helsinki jobs',
  },
  jobsInTampere: {
    id: 'footer.jobsInTampere',
    defaultMessage: 'Jobs in Tampere',
    description: 'Footer link to Tampere jobs',
  },
  jobsInEspoo: {
    id: 'footer.jobsInEspoo',
    defaultMessage: 'Jobs in Espoo',
    description: 'Footer link to Espoo jobs',
  },
  jobsInTurku: {
    id: 'footer.jobsInTurku',
    defaultMessage: 'Jobs in Turku',
    description: 'Footer link to Turku jobs',
  },
  jobsInOulu: {
    id: 'footer.jobsInOulu',
    defaultMessage: 'Jobs in Oulu',
    description: 'Footer link to Oulu jobs',
  },
  remoteJobs: {
    id: 'footer.remoteJobs',
    defaultMessage: 'Remote jobs',
    description: 'Footer link to global remote jobs',
  },
  allLocations: {
    id: 'footer.allLocations',
    defaultMessage: 'All locations',
    description: 'Footer link to the locations hub page (arrow glyph added in markup, not translated)',
  },
  browseAllPages: {
    id: 'footer.browseAllPages',
    defaultMessage: 'Browse all pages',
    description: 'Footer link to the HTML sitemap, emphasised inside the Find jobs column',
  },
  salariesByRole: {
    id: 'footer.salariesByRole',
    defaultMessage: 'Salaries by role',
    description: 'Footer link to the salary explorer',
  },
  rolesIndex: {
    id: 'footer.rolesIndex',
    defaultMessage: 'Jobs by role',
    description: 'Footer link to the roles index (every role hub with its live job count)',
  },
  bestEmployers: {
    id: 'footer.bestEmployers',
    defaultMessage: 'Employers',
    description: 'Footer link to the employers index (employers hiring now)',
  },
  collectiveAgreementsLink: {
    id: 'footer.collectiveAgreementsLink',
    defaultMessage: 'Collective agreements',
    description: 'Footer link to the collective agreements page',
  },
  guidesAndArticles: {
    id: 'footer.guidesAndArticles',
    defaultMessage: 'Guides & articles',
    description: 'Footer link to the resources/guides hub',
  },
  featuresLink: {
    id: 'footer.featuresLink',
    defaultMessage: 'Features',
    description: 'Footer link to the /features hub page explaining what JobCrawls offers job seekers',
  },
  signIn: {
    id: 'footer.signIn',
    defaultMessage: 'Sign in',
    description: 'Footer link to sign in',
  },
  aboutJobcrawls: {
    id: 'footer.aboutJobcrawls',
    defaultMessage: 'About JobCrawls',
    description: 'Footer link to the about page',
  },
  partnerships: {
    id: 'footer.partnerships',
    defaultMessage: 'Media partnerships',
    description: 'Footer link to media partnerships page',
  },
  editorialStandards: {
    id: 'footer.editorialStandards',
    defaultMessage: 'Editorial standards',
    description: 'Footer link to editorial standards page',
  },
  howCrawlerWorks: {
    id: 'footer.howCrawlerWorks',
    defaultMessage: 'How our crawler works',
    description: 'Footer link to the /bot crawler-disclosure page',
  },
  forEmployersLink: {
    id: 'footer.forEmployersLink',
    defaultMessage: 'For employers',
    description: 'Footer link to the for-employers page',
  },
  freeCareersPageLink: {
    id: 'footer.freeCareersPageLink',
    defaultMessage: 'Careers page',
    description: 'Footer link to the hosted careers page product page',
  },
  forDevelopers: {
    id: 'footer.forDevelopers',
    defaultMessage: 'For Developers',
    description: 'Footer link to the /connector developer/AI-integration docs page',
  },
  connectorPrivacy: {
    id: 'footer.connectorPrivacy',
    defaultMessage: 'Connector privacy',
    description: 'Footer link to the AI connector section of the main /privacy policy',
  },
  contact: {
    id: 'footer.contact',
    defaultMessage: 'Contact',
    description: 'Footer link to contact page',
  },
  privacy: {
    id: 'footer.privacy',
    defaultMessage: 'Privacy Policy',
    description: 'Footer link to privacy policy',
  },
  terms: {
    id: 'footer.terms',
    defaultMessage: 'Terms of Service',
    description: 'Footer link to terms of service',
  },
  cookieSettings: {
    id: 'footer.cookieSettings',
    defaultMessage: 'Cookie settings',
    description: 'Footer link/button to open cookie preferences',
  },
  accessibilityStatementLink: {
    id: 'footer.accessibilityStatementLink',
    defaultMessage: 'Accessibility statement',
    description: 'Footer link to the accessibility statement page',
  },
  sitemap: {
    id: 'footer.sitemap',
    defaultMessage: 'Site Map',
    description: 'Footer link to the HTML site map page listing every page category',
  },
});
